<!-- src/layout/AppTopbar.vue -->
<script setup>
import { useLayout } from '@/layout/composables/layout';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
const router = useRouter();
import { useI18n } from 'vue-i18n';
const { t } = useI18n();
import { TenantService } from '@/config/tenant';

const currentTenant = computed(() => {
    const tenant = TenantService.getCurrentTenantName();
    return {
        fullName: tenant.charAt(0).toUpperCase() + tenant.slice(1),
        firstLetter: tenant.charAt(0).toUpperCase()
    };
});

import { useAuthStore } from '@/stores/auth';
const authStore = useAuthStore();

// ✅ Avatar: Varsa göster, yoksa blank-avatar
const userAvatar = computed(() => {
    return authStore.user?.avatar || '/layout/images/avatar/blank-avatar.png';
});

const { onMenuToggle, onTopbarMenuToggle, onConfigSidebarToggle } = useLayout();

const hasPermission = (permission) => {
    if (Array.isArray(permission)) {
        return permission.some((perm) => authStore.allowedLinks.includes(perm));
    } else {
        return !permission || authStore.allowedLinks.includes(permission);
    }
};

const item = computed(() => {
    const items = [
        {
            label: 'Kullanıcılar',
            permission: ['is_yonetici', 'is_superuser'],
            command: () => {
                router.push('/user-management');
            }
        },
        {
            label: 'Satışlar',
            permission: ['is_yonetici', 'is_superuser'],
            command: () => {
                router.push('/orders');
            }
        },
        {
            label: 'İşletme Hesabı Basvuru Formu',
            permission: ['is_yonetici', 'is_superuser', 'is_musteri', 'is_bayi', 'is_personel'],
            command: () => {
                router.push('/apply/register');
            }
        },
    ];
    return items.filter((item) => hasPermission(item.permission));
});

const tenantContact = computed(() => {
    const tenant = authStore.currentTenant;
    return {
        name: tenant?.name_top || '',
        phone: tenant?.phone || ''
    };
});

const onConfigButtonClick = () => {
    onConfigSidebarToggle();
};

const logauth = () => {
    authStore.logout();
};
</script>

<template>
    <div class="layout-topbar">

        <div class="layout-topbar-start">
            <router-link class="layout-topbar-logo" to="/">
                <svg width="167" height="49" viewBox="0 0 167 32" fill="none" xmlns="http://www.w3.org/2000/svg"
                    class="layout-topbar-logo-full">
                    <defs id="defs2" />
                    <text xml:space="preserve" style="font-size: 22.3168px; fill: #000000; stroke-width: 1.85973"
                        x="4.3707757" y="23.979361" id="text2" transform="scale(0.83571163,1.196585)">
                        <tspan id="tspan2" x="4.3707757" y="23.979361" style="fill: #ffffff; stroke-width: 1.85973">
                            {{ currentTenant.fullName }}
                        </tspan>
                    </text>
                </svg>
                <svg width="41" height="49" viewBox="0 0 41 32" fill="none" xmlns="http://www.w3.org/2000/svg"
                    class="layout-topbar-logo-slim">
                    <text xml:space="preserve" style="font-size: 47.9715px; fill: #000000; stroke-width: 3.99763"
                        x="4.3707757" y="38.105625" id="text3" transform="scale(1.0623966,0.94126808)">
                        <tspan id="tspan3" x="4.3707757" y="38.105625" style="fill: #ffffff; stroke-width: 3.99763">
                            {{ currentTenant.firstLetter }}
                        </tspan>
                    </text>
                </svg>
            </router-link>
            <a ref="menuButton" class="layout-menu-button" @click="onMenuToggle">
                <i class="pi pi-chevron-right"></i>
            </a>

            <a ref="mobileMenuButton" class="layout-topbar-mobile-button" @click="onTopbarMenuToggle">
                <i class="pi pi-ellipsis-v"></i>
            </a>
        </div>

        <div class="layout-topbar-end">
            <div class="layout-topbar-actions-start">
                <MegaMenu :model="item" class="layout-megamenu hidden lg:block" breakpoint="0px"></MegaMenu>
            </div>
            <div class="layout-topbar-actions-end">
                <ul class="layout-topbar-items">
                    <li v-if="tenantContact.name && tenantContact.phone"
                        class="flex items-center gap-1.5 text-xs md:text-sm md:gap-2">
                        <i class="pi pi-user text-xs"></i>
                        <span class="font-medium truncate max-w-[80px] md:max-w-none">{{ tenantContact.name }}</span>
                        <i class="pi pi-phone text-xs"></i>
                        <span class="whitespace-nowrap">{{ tenantContact.phone }}</span>
                    </li>
                    <li>
                        <a v-styleclass="{ selector: '@next', enterFromClass: 'hidden', enterActiveClass: 'animate-scalein', leaveToClass: 'hidden', leaveActiveClass: 'animate-fadeout', hideOnOutsideClick: true }"
                            class="">
                            <OverlayBadge severity="warn" :pt="{ pcBadge: { root: '!outline-0' } }">
                                <i class="pi pi-bell"></i>
                            </OverlayBadge>
                        </a>
                        <div class="hidden">
                            <ul class="list-none p-0 m-0">
                                <li class="px-4 py-1">
                                    <span>Bildirim Yok</span>
                                </li>
                            </ul>
                        </div>
                    </li>

                    <li>
                        <a v-styleclass="{ selector: '@next', enterFromClass: 'hidden', enterActiveClass: 'animate-scalein', leaveToClass: 'hidden', leaveActiveClass: 'animate-fadeout', hideOnOutsideClick: true }"
                            class="">
                            <img :src="userAvatar" alt="avatar" class="w-8 h-8 rounded-full object-cover" />
                        </a>
                        <div class="hidden absolute right-0 mt-2 w-48 z-50">
                            <ul class="list-none p-0 m-0 bg-surface-0 dark:bg-surface-900 rounded-border shadow-lg">
                                <li>
                                    <a class="cursor-pointer flex items-center hover:bg-emphasis duration-150 transition-all px-4 py-2"
                                        @click="router.push('/user/profile')">
                                        <i class="pi pi-file-o mr-2"></i>
                                        <span>Profil</span>
                                    </a>
                                </li>
                                <li>
                                    <a class="cursor-pointer flex items-center hover:bg-emphasis duration-150 transition-all px-4 py-2"
                                        @click="logauth()">
                                        <i class="pi pi-power-off mr-2"></i>
                                        <span>{{ t('auth.log_out') }}</span>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </li>

                    <li>
                        <a @click="onConfigButtonClick()" class="p-ripple" v-ripple>
                            <i class="pi pi-palette"></i>
                        </a>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>